import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const ids = new Set(['15725226754374', '15725226328390', '15725226000710', '15726707343686', '15623340753222', '15760655614278', '15760641327430', '15530924507462', '15749168136518', '15749172560198']);
const colorKey = value => ({ negro: 'black', blanco: 'white' }[value.toLowerCase()] || value.toLowerCase());

class ClothingDesignPicker extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (!ids.has(this.dataset.productId)) return;
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.addEventListener('click', event => {
      const link = event.target.closest('a[data-design-product]');
      if (link?.getAttribute('aria-disabled') === 'true') event.preventDefault();
    }, { signal });
    document.addEventListener('change', () => this.updateLinks(), { signal });
    document.addEventListener(ThemeEvents.variantUpdate, () => this.updateLinks(), { signal });
    this.updateLinks();
  }
  updatedCallback() { super.updatedCallback(); this.updateLinks(); }
  disconnectedCallback() { this.controller?.abort(); super.disconnectedCallback(); }
  updateLinks() {
    const picker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"][data-template-product-match="true"]`);
    if (!picker) return;
    const selected = [...picker.querySelectorAll('input:checked')].map(input => input.value);
    if (selected.length !== 2) return;
    const products = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    for (const link of this.querySelectorAll('a[data-design-product]')) {
      const product = products.find(product => String(product.id) === link.dataset.designProduct);
      let variant = product?.variants.find(variant => colorKey(variant.color) === colorKey(selected[0]) && variant.size === selected[1] && variant.available);
      // A missing Burgundy size does not make the discipline itself unavailable.
      if (!variant && ['15725226754374', '15725226328390', '15725226000710'].includes(this.dataset.productId) && colorKey(selected[0]) === 'burgundy') {
        variant = product?.variants.find(variant => colorKey(variant.color) === 'burgundy' && variant.available);
      }
      link.setAttribute('aria-disabled', String(!variant));
      link.title = variant ? link.dataset.designName : `${link.dataset.designName}: ${selected.join(' / ')} no disponible`;
      link.querySelector('[data-unavailable]').hidden = Boolean(variant);
      if (variant) {
        const url = new URL(product.url, location.origin);
        url.searchParams.set('variant', variant.id);
        url.searchParams.set('view', this.dataset.template);
        link.href = url.href;
      } else link.removeAttribute('href');
    }
  }
}
if (!customElements.get('ms-clothing-design-picker')) customElements.define('ms-clothing-design-picker', ClothingDesignPicker);

function selectClothingColor(event) {
  const swatch = event.target instanceof Element ? event.target.closest('[data-ms-clothing-variant][data-ms-clothing-prefix]') : null;
  const card = swatch?.closest('product-card[data-ms-clothing-card]');
  if (!card || !ids.has(card.dataset.productId)) return;
  if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (card.dataset.msClothingVariant === swatch.dataset.msClothingVariant) return;
  const slideshow = card.querySelector('[ref="cardGallery"] slideshow-component');
  if (!slideshow || typeof slideshow.select !== 'function') return;
  const slides = [...slideshow.querySelectorAll('slideshow-slide')];
  const matching = mobileClothingPair(slides.filter(slide => slide.dataset.msClothingPrefix === swatch.dataset.msClothingPrefix));
  const first = matching.find(slide => slide.dataset.msClothingFront === 'true') || matching[0];
  if (!first) return;
  for (const slide of slides) {
    slide.hidden = !matching.includes(slide);
    slide.removeAttribute('reveal');
  }
  slideshow.select({ id: first.getAttribute('slide-id') }, undefined, { animate: false });
  const scroller = slideshow.querySelector('[ref="scroller"]');
  if (scroller) scroller.scrollTo({ left: scroller.scrollLeft + first.getBoundingClientRect().left - scroller.getBoundingClientRect().left, behavior: 'instant' });
  card.dataset.msClothingVariant = swatch.dataset.msClothingVariant;
  for (const link of card.querySelectorAll('a[href]')) {
    const url = new URL(link.href, location.href);
    if (url.origin === location.origin && url.pathname.startsWith('/products/')) {
      url.searchParams.set('variant', swatch.dataset.msClothingVariant);
      url.searchParams.set('view', card.dataset.msClothingTemplate);
      link.href = url.href;
    }
  }
  const image = first.querySelector('img');
  if (image) card.dataset.featuredMediaUrl = image.currentSrc || image.src;
  for (const option of card.querySelectorAll('[data-ms-clothing-variant]')) option.setAttribute('aria-pressed', String(option === swatch));
  card.querySelector('quick-add-component')?.fetchProductPage?.(card.querySelector('[ref="productCardLink"]').href);
}
function selectClothingSlide(event) {
  const arrow = event.target instanceof Element ? event.target.closest('button.slideshow-control--next, button.slideshow-control--previous') : null;
  const card = arrow?.closest('product-card[data-ms-clothing-card]');
  if (!card || !ids.has(card.dataset.productId)) return;
  const slideshow = arrow.closest('slideshow-component');
  if (slideshow !== card.querySelector('[ref="cardGallery"] slideshow-component')) return;
  const scroller = slideshow.querySelector('[ref="scroller"]');
  const slides = [...slideshow.querySelectorAll('slideshow-slide[data-ms-clothing-prefix]:not([hidden])')];
  if (!scroller || !slides.length) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const bounds = scroller.getBoundingClientRect();
  const center = bounds.left + bounds.width / 2;
  let current = 0;
  let distance = Infinity;
  slides.forEach((slide, index) => {
    const rect = slide.getBoundingClientRect();
    const nextDistance = Math.abs(rect.left + rect.width / 2 - center);
    if (nextDistance < distance) { current = index; distance = nextDistance; }
  });
  const direction = arrow.classList.contains('slideshow-control--next') ? 1 : -1;
  const target = slides[Math.max(0, Math.min(slides.length - 1, current + direction))];
  slideshow.select({ id: target.getAttribute('slide-id') }, undefined, { animate: false });
  scroller.scrollTo({ left: scroller.scrollLeft + target.getBoundingClientRect().left - bounds.left, behavior: 'instant' });
}
document.addEventListener('click', selectClothingSlide, true);
document.addEventListener('click', selectClothingColor, true);
document.addEventListener('keydown', selectClothingColor, true);

document.addEventListener(ThemeEvents.variantUpdate, event => {
  const productId = String(event.detail?.data?.productId || '');
  if (!ids.has(productId)) return;
  requestAnimationFrame(() => {
    const gallery = document.querySelector(`media-gallery[data-ms-clothing-product="${productId}"]`);
    const slideshow = gallery?.querySelector('slideshow-component');
    const first = slideshow?.querySelector('slideshow-slide:not([hidden])');
    if (first && typeof slideshow.select === 'function') slideshow.select(0, undefined, { animate: false });
  });
});

function prepareClothingLinks() {
  for (const card of document.querySelectorAll('product-card[data-ms-clothing-card]')) {
    if (!ids.has(card.dataset.productId)) continue;
    for (const link of card.querySelectorAll('a[href]')) {
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || !url.pathname.startsWith('/products/')) continue;
      if (url.searchParams.get('view') === card.dataset.msClothingTemplate) continue;
      url.searchParams.set('view', card.dataset.msClothingTemplate);
      link.href = url.href;
    }
  }
}
prepareClothingLinks();
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', prepareClothingLinks, { once: true });
const clothingObserver = new MutationObserver(records => {
  if (records.some(record => [...record.addedNodes].some(node => node instanceof Element && (node.matches('product-card[data-ms-clothing-card]') || node.querySelector('product-card[data-ms-clothing-card]'))))) prepareClothingLinks();
});
clothingObserver.observe(document.documentElement, { childList: true, subtree: true });

// Only the three discipline tees: Burgundy has S–2XL, never 3XL.
const burgundyTeeIds = new Set(['15725226754374', '15725226328390', '15725226000710']);
const burgundySizeStyle = document.createElement('style');
burgundySizeStyle.textContent = [...burgundyTeeIds].map(id =>
  'variant-picker[data-product-id="' + id + '"][data-template-product-match="true"]:has(input[value="Burgundy"]:checked) label:has(input[value="3XL"]) { display: none !important; }'
).join(String.fromCharCode(10));
document.head.append(burgundySizeStyle);

document.addEventListener('change', event => {
  const color = event.target;
  if (!(color instanceof HTMLInputElement) || color.value !== 'Burgundy') return;
  const picker = color.closest('variant-picker[data-template-product-match="true"]');
  if (!picker || !burgundyTeeIds.has(picker.dataset.productId)) return;
  const size = picker.querySelector('input[value="3XL"]:checked');
  if (!size) return;
  const fallback = picker.querySelector('input[value="2XL"]');
  if (!fallback || typeof picker.updateSelectedOption !== 'function') return;
  picker.updateSelectedOption(fallback);
  const designPicker = document.querySelector('ms-clothing-design-picker[data-product-id="' + picker.dataset.productId + '"]');
  const products = JSON.parse(designPicker?.querySelector('script[type="application/json"]')?.textContent || '[]');
  const product = products.find(product => String(product.id) === picker.dataset.productId);
  const variant = product?.variants.find(variant => variant.color === 'Burgundy' && variant.size === '2XL' && variant.available);
  if (variant) color.dataset.variantId = String(variant.id);
}, true);

// Mobile product galleries: one front and one back for the selected color.
// Keep an untouched copy so desktop rendering and resize behavior are preserved.
const mobileClothingViewport = matchMedia('(max-width: 749px)');
const mobileClothingOriginals = new WeakMap();
const mobileClothingGalleryIds = new Set([...ids, '15773682270534', '15773188686150']);
function prepareMobileClothingGallery(gallery) {
  if (!mobileClothingViewport.matches || !mobileClothingGalleryIds.has(gallery?.dataset.msClothingProduct) || mobileClothingOriginals.has(gallery)) return;
  const slideshow = gallery.querySelector('slideshow-component');
  const slides = [...(slideshow?.querySelectorAll('slideshow-slides > slideshow-slide') || [])];
  const filename = slide => slide.querySelector('img')?.getAttribute('src')?.split('?')[0] || '';
  const colorPrefix = filename(slides[0]).split(/-(?:front|back)-/)[0];
  const front = slides.findIndex(slide => filename(slide).startsWith(colorPrefix + '-front-'));
  const back = slides.findIndex(slide => filename(slide).startsWith(colorPrefix + '-back-'));
  if (front < 0 || back < 0 || slides.length <= 2) return;
  const keep = [front, back];
  mobileClothingOriginals.set(gallery, gallery.cloneNode(true));
  const trim = nodes => [...nodes].forEach((node, index) => { if (!keep.includes(index)) node.remove(); });
  trim(slides);
  keep.forEach((oldIndex, index) => {
    const slide = slides[oldIndex];
    slide.style.setProperty('--slideshow-timeline', '--slide-' + index);
    slide.setAttribute('aria-hidden', String(index !== 0));
    slide.removeAttribute('reveal');
    const action = slide.getAttribute('on:click');
    if (action) slide.setAttribute('on:click', action.replace(/\/open\/\d+$/, '/open/' + index));
  });
  slideshow.setAttribute('initial-slide', '0');
  slideshow.style.setProperty('--slideshow-timeline', '--slide-0, --slide-1');
  for (const controls of gallery.querySelectorAll('slideshow-controls')) {
    for (const list of controls.querySelectorAll('ol')) trim(list.children);
    [...controls.querySelectorAll('ol button')].forEach((button, index) => {
      button.setAttribute('aria-label', 'Diapositiva ' + (index + 1) + ' de 2');
      button.style.setProperty('animation-timeline', '--slide-' + index);
      const action = button.getAttribute('on:click');
      if (action) button.setAttribute('on:click', action.replace(/\/select\/\d+$/, '/select/' + index));
    });
    const counter = controls.querySelector('.slideshow-controls__counter');
    if (counter) counter.innerHTML = '<span ref="current">1</span><span class="slash">/</span>2';
  }
  for (const grid of gallery.querySelectorAll('.media-gallery__grid, .dialog-zoomed-gallery')) trim(grid.children);
  const thumbnails = gallery.querySelector('.dialog-thumbnails-list');
  if (thumbnails) {
    trim(thumbnails.children);
    [...thumbnails.children].forEach((button, index) => {
      for (const attribute of ['on:click', 'on:pointerenter']) {
        const action = button.getAttribute(attribute);
        if (action) button.setAttribute(attribute, action.replace(/\/\d+$/, '/' + index));
      }
    });
  }
  if (gallery.isConnected) requestAnimationFrame(() => slideshow.select?.(0, undefined, { animate: false }));
}
function prepareMobileClothingGalleries() {
  for (const gallery of document.querySelectorAll('media-gallery[data-ms-clothing-product]')) {
    if (mobileClothingViewport.matches) prepareMobileClothingGallery(gallery);
    else {
      const original = mobileClothingOriginals.get(gallery);
      if (original) gallery.replaceWith(original);
    }
  }
}
// Filter the detached variant response before the native gallery is connected.
document.addEventListener(ThemeEvents.variantUpdate, event => {
  const gallery = event.detail?.data?.html?.querySelector('media-gallery[data-ms-clothing-product]');
  if (gallery) prepareMobileClothingGallery(gallery);
}, true);
prepareMobileClothingGalleries();
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', prepareMobileClothingGalleries, { once: true });
mobileClothingViewport.addEventListener('change', prepareMobileClothingGalleries);
const mobileClothingGalleryObserver = new MutationObserver(records => {
  if (records.some(record => [...record.addedNodes].some(node => node instanceof Element && (node.matches('media-gallery[data-ms-clothing-product]') || node.querySelector('media-gallery[data-ms-clothing-product]'))))) prepareMobileClothingGalleries();
});
mobileClothingGalleryObserver.observe(document.documentElement, { childList: true, subtree: true });

// Apply the same mobile-only pair to collection cards, preserving their variant links.
function mobileClothingPair(slides) {
  if (!matchMedia('(max-width: 749px)').matches) return slides;
  const front = slides.find(slide => slide.querySelector('img')?.getAttribute('src')?.includes('-front-'));
  const back = slides.find(slide => slide.querySelector('img')?.getAttribute('src')?.includes('-back-'));
  return front && back ? [front, back] : slides;
}
const mobileClothingCards = new WeakSet();
function prepareMobileClothingCards() {
  for (const card of document.querySelectorAll('product-card[data-ms-clothing-card]')) {
    if (!ids.has(card.dataset.productId)) continue;
    if (!mobileClothingViewport.matches && !mobileClothingCards.has(card)) continue;
    const slides = [...card.querySelectorAll('[ref="cardGallery"] slideshow-slide[data-ms-clothing-prefix]')];
    const visible = slides.find(slide => !slide.hidden);
    if (!visible) continue;
    const matching = slides.filter(slide => slide.dataset.msClothingPrefix === visible.dataset.msClothingPrefix);
    const keep = mobileClothingPair(matching);
    for (const slide of slides) {
      slide.hidden = !keep.includes(slide);
      slide.removeAttribute('reveal');
    }
    if (mobileClothingViewport.matches) mobileClothingCards.add(card);
    else mobileClothingCards.delete(card);
  }
}
prepareMobileClothingCards();
mobileClothingViewport.addEventListener('change', prepareMobileClothingCards);
const mobileClothingCardObserver = new MutationObserver(records => {
  if (records.some(record => [...record.addedNodes].some(node => node instanceof Element && (node.matches('product-card[data-ms-clothing-card]') || node.querySelector('product-card[data-ms-clothing-card]'))))) prepareMobileClothingCards();
});
mobileClothingCardObserver.observe(document.documentElement, { childList: true, subtree: true });
